import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Pagination } from '../components/ui/Pagination';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { AlertCircle, CheckCircle, XCircle, Clock, Filter, DollarSign, MessageSquare, Eye, User, Building2, AlertTriangle, ArrowRight, Check, X, Search, TrendingUp, Calendar, MoreHorizontal } from 'lucide-react';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useToast } from '../components/ui/Toast';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { motion, AnimatePresence } from 'framer-motion';
interface Claim {
  id: string;
  orderId: string;
  orderNo: string;
  customerName: string;
  company: string;
  branch: string;
  type: string;
  status: string;
  priority: string;
  description: string;
  resolution: string | null;
  createdAt: string;
  resolvedAt: string | null;
  createdBy: string;
  createdById?: string;
}
export function ClaimsPage() {
  useDocumentTitle('Reclamaciones');
  const [claims, setClaims] = useState<Claim[]>([]);
  const [filteredClaims, setFilteredClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  // Accept modal state
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [acceptResolution, setAcceptResolution] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [applyRefund, setApplyRefund] = useState(false);
  const [applyRejectRefund, setApplyRejectRefund] = useState(false);
  const [rejectRefundAmount, setRejectRefundAmount] = useState('');
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);
  const paginatedClaims = filteredClaims.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => {
    loadClaims();
    const subscription = supabaseClient.subscribeToClaims(() => {
      loadClaims();
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  useEffect(() => {
    filterClaims();
  }, [claims, searchQuery, statusFilter, priorityFilter]);
  const loadClaims = async () => {
    try {
      setLoading(true);
      const data = await supabaseClient.getClaims();
      setClaims(data);
    } catch (error) {
      console.error('Error loading claims:', error);
    } finally {
      setLoading(false);
    }
  };
  const filterClaims = () => {
    let filtered = claims;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(claim => claim.status === statusFilter);
    }
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(claim => claim.priority === priorityFilter);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(claim => claim.orderNo.toLowerCase().includes(query) || claim.customerName.toLowerCase().includes(query) || claim.company.toLowerCase().includes(query) || claim.description.toLowerCase().includes(query));
    }
    setFilteredClaims(filtered);
    setCurrentPage(1);
  };
  const handleAcceptClaim = (claim: Claim) => {
    setSelectedClaim(claim);
    setRefundAmount('');
    setAcceptResolution('');
    setApplyRefund(false);
    setShowAcceptModal(true);
  };
  const handleRejectClaim = (claim: Claim) => {
    setSelectedClaim(claim);
    setRejectReason('');
    setApplyRejectRefund(false);
    setRejectRefundAmount('');
    setShowRejectModal(true);
  };
  const handleViewDetail = (claim: Claim) => {
    setSelectedClaim(claim);
    setShowDetailModal(true);
  };
  const confirmAcceptClaim = async () => {
    if (!selectedClaim) return;
    try {
      let resolution = acceptResolution || 'Reclamación aceptada por el administrador';
      if (applyRefund && refundAmount) {
        resolution += ` | Reembolso aprobado: RD$ ${parseFloat(refundAmount).toFixed(2)}`;
      }
      await supabaseClient.updateClaim(selectedClaim.id, {
        status: 'Aceptada',
        resolvedAt: new Date().toISOString(),
        resolution
      });
      // Apply actual balance refund if requested
      if (applyRefund && refundAmount) {
        const amount = parseFloat(refundAmount);
        if (amount > 0) {
          const now = new Date();
          // Resolve employee ID: use createdById or fall back to name lookup
          let employeeId = selectedClaim.createdById;
          if (!employeeId) {
            try {
              const employees = await supabaseClient.getEmployees();
              const emp = employees.find((e: any) => e.name?.toLowerCase() === selectedClaim.customerName?.toLowerCase());
              employeeId = emp?.id;
            } catch (_) {}
          }
          if (employeeId) {
            try {
              await supabaseClient.refundBalance(employeeId, now.getMonth() + 1, now.getFullYear(), amount, 'Admin', `Reembolso por reclamación #${selectedClaim.orderNo}`);
            } catch (refundErr) {
              console.error('[Claims] Refund error (non-critical):', refundErr);
              showToast('warning', 'Reembolso parcial', 'Reclamación aceptada pero no se pudo aplicar el reembolso al balance automáticamente.');
            }
          } else {
            showToast('warning', 'Sin empleado', 'No se encontró el empleado para aplicar el reembolso.');
          }
        }
      }
      addLog('Reclamación aceptada' + (applyRefund ? ' con reembolso' : ''), `Orden #${selectedClaim.orderNo} - ${selectedClaim.customerName}${applyRefund ? ` - Reembolso: RD$ ${refundAmount}` : ''}`, 'claim');
      showToast('success', 'Reclamación aceptada', `Orden #${selectedClaim.orderNo} resuelta${applyRefund && refundAmount ? ` · Reembolso RD$ ${refundAmount} aplicado` : ''}`);
      setShowAcceptModal(false);
      setSelectedClaim(null);
      loadClaims();
    } catch (error) {
      console.error('Error accepting claim:', error);
      showToast('error', 'Error', 'No se pudo aceptar la reclamación');
    }
  };
  const confirmRejectClaim = async () => {
    if (!selectedClaim || !rejectReason.trim()) {
      showToast('warning', 'Requerido', 'Ingresa el motivo del rechazo');
      return;
    }
    try {
      let resolution = rejectReason.trim();
      if (applyRejectRefund && rejectRefundAmount) {
        resolution += ` | Reembolso aplicado: RD$ ${parseFloat(rejectRefundAmount).toFixed(2)}`;
      }
      await supabaseClient.updateClaim(selectedClaim.id, {
        status: 'Rechazada',
        resolvedAt: new Date().toISOString(),
        resolution
      });
      // Apply balance refund even on rejection if admin chose to
      if (applyRejectRefund && rejectRefundAmount) {
        const amount = parseFloat(rejectRefundAmount);
        if (amount > 0) {
          const now = new Date();
          let employeeId = selectedClaim.createdById;
          if (!employeeId) {
            try {
              const employees = await supabaseClient.getEmployees();
              const emp = employees.find((e: any) => e.name?.toLowerCase() === selectedClaim.customerName?.toLowerCase());
              employeeId = emp?.id;
            } catch (_) {}
          }
          if (employeeId) {
            try {
              await supabaseClient.refundBalance(employeeId, now.getMonth() + 1, now.getFullYear(), amount, 'Admin', `Reembolso parcial por reclamación #${selectedClaim.orderNo} (rechazada)`);
            } catch (refundErr) {
              console.error('[Claims] Refund error (non-critical):', refundErr);
              showToast('warning', 'Reembolso parcial', 'Reclamación rechazada pero no se pudo aplicar el reembolso al balance.');
            }
          }
        }
      }
      addLog('Reclamación rechazada', `Orden #${selectedClaim.orderNo} - ${selectedClaim.customerName}${applyRejectRefund ? ` - Reembolso: RD$ ${rejectRefundAmount}` : ''}`, 'claim');
      showToast('info', 'Reclamación rechazada', `Orden #${selectedClaim.orderNo}`);
      setShowRejectModal(false);
      setSelectedClaim(null);
      loadClaims();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      showToast('error', 'Error', 'No se pudo rechazar la reclamación');
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Pendiente':
        return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800';
      case 'Aceptada':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800';
      case 'Rechazada':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Alta':
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
      case 'Media':
        return 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800';
      case 'Baja':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  const getPriorityBorderColor = (priority: string) => {
    switch (priority) {
      case 'Alta':
        return 'border-l-red-500';
      case 'Media':
        return 'border-l-orange-500';
      case 'Baja':
        return 'border-l-blue-500';
      default:
        return 'border-l-gray-300';
    }
  };
  const pendingCount = claims.filter(c => c.status === 'Pendiente').length;
  const acceptedCount = claims.filter(c => c.status === 'Aceptada').length;
  const rejectedCount = claims.filter(c => c.status === 'Rechazada').length;
  const resolutionRate = claims.length > 0 ? Math.round((acceptedCount + rejectedCount) / claims.length * 100) : 0;
  const pendingClaims = claims.filter(c => c.status === 'Pendiente');
  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-950" data-id="element-1599">
        <div className="text-center" data-id="element-1600">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4" data-id="element-1601"></div>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1602">
            Cargando reclamaciones...
          </p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 dark:bg-[#050505] pb-20" data-id="element-1603">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-20" data-id="element-1604">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6" data-id="element-1605">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1606">
            <div data-id="element-1607">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3" data-id="element-1608">
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-1609">
                  <AlertTriangle className="w-6 h-6 text-gray-700 dark:text-gray-300" data-id="element-1610" />
                </div>
                Gestión de Reclamaciones
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm" data-id="element-1611">
                Administra y resuelve los reportes de incidentes
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-1612">
              <div className="text-right hidden md:block" data-id="element-1613">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1614">
                  Tasa de Resolución
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1615">
                  {resolutionRate}%
                </p>
              </div>
              <div className="h-10 w-px bg-gray-200 dark:bg-gray-800 hidden md:block" data-id="element-1616"></div>
              <Button onClick={loadClaims} variant="outline" size="sm" data-id="element-1617">
                Actualizar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-8" data-id="element-1618">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1619">
          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm relative overflow-hidden group" data-id="element-1620">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-1621">
              <MessageSquare size={60} className="text-gray-900 dark:text-white" data-id="element-1622" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-1623">
              Total Reclamaciones
            </p>
            <p className="text-4xl font-bold text-gray-900 dark:text-white mt-2" data-id="element-1624">
              {claims.length}
            </p>
            <div className="mt-4 flex items-center text-xs font-medium text-gray-500" data-id="element-1625">
              <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-md" data-id="element-1626">
                Histórico completo
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-amber-200 dark:border-amber-900/50 shadow-sm relative overflow-hidden group ring-1 ring-amber-100 dark:ring-amber-900/20" data-id="element-1627">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-1628">
              <Clock size={60} className="text-amber-600" data-id="element-1629" />
            </div>
            <div className="flex items-center gap-2" data-id="element-1630">
              <p className="text-sm font-bold text-amber-700 dark:text-amber-400" data-id="element-1631">
                Pendientes
              </p>
              {pendingCount > 0 && <span className="flex h-2 w-2 relative" data-id="element-1632">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-1633"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" data-id="element-1634"></span>
                </span>}
            </div>
            <p className="text-4xl font-bold text-amber-700 dark:text-amber-400 mt-2" data-id="element-1635">
              {pendingCount}
            </p>
            <div className="mt-4 flex items-center text-xs font-medium text-amber-700/70 dark:text-amber-400/70" data-id="element-1636">
              <span className="bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-md" data-id="element-1637">
                Requieren atención
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-emerald-200 dark:border-emerald-900/50 shadow-sm relative overflow-hidden group" data-id="element-1638">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-1639">
              <CheckCircle size={60} className="text-emerald-600" data-id="element-1640" />
            </div>
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400" data-id="element-1641">
              Aceptadas
            </p>
            <p className="text-4xl font-bold text-emerald-700 dark:text-emerald-400 mt-2" data-id="element-1642">
              {acceptedCount}
            </p>
            <div className="mt-4 flex items-center text-xs font-medium text-emerald-700/70 dark:text-emerald-400/70" data-id="element-1643">
              <span className="bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-md" data-id="element-1644">
                Resueltas favorablemente
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-900/50 shadow-sm relative overflow-hidden group" data-id="element-1645">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-1646">
              <XCircle size={60} className="text-red-600" data-id="element-1647" />
            </div>
            <p className="text-sm font-medium text-red-700 dark:text-red-400" data-id="element-1648">
              Rechazadas
            </p>
            <p className="text-4xl font-bold text-red-700 dark:text-red-400 mt-2" data-id="element-1649">
              {rejectedCount}
            </p>
            <div className="mt-4 flex items-center text-xs font-medium text-red-700/70 dark:text-red-400/70" data-id="element-1650">
              <span className="bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-md" data-id="element-1651">
                Desestimadas
              </span>
            </div>
          </div>
        </div>

        {/* Urgent Pending Section */}
        {pendingClaims.length > 0 && <div className="space-y-4" data-id="element-1652">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400" data-id="element-1653">
              <AlertCircle size={20} data-id="element-1654" />
              <h2 className="text-lg font-bold uppercase tracking-wide" data-id="element-1655">
                Requieren Atención Inmediata ({pendingClaims.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-1656">
              {pendingClaims.map(claim => <motion.div key={claim.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white dark:bg-gray-900 rounded-xl border-l-4 border-l-amber-500 border-y border-r border-gray-200 dark:border-gray-800 shadow-md p-5 flex flex-col justify-between" data-id="element-1657">
                  <div data-id="element-1658">
                    <div className="flex justify-between items-start mb-3" data-id="element-1659">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200" data-id="element-1660">
                        #{claim.orderNo}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${getPriorityBadge(claim.priority)}`} data-id="element-1661">
                        {claim.priority}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white mb-1" data-id="element-1662">
                      {claim.customerName}
                    </h3>
                    <p className="text-xs text-gray-500 mb-3 flex items-center gap-1" data-id="element-1663">
                      <Building2 size={12} data-id="element-1664" /> {claim.company}
                    </p>
                    <div className="bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg mb-4" data-id="element-1665">
                      <p className="text-xs font-bold text-amber-800 dark:text-amber-300 mb-1 uppercase" data-id="element-1666">
                        {claim.type}
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2" data-id="element-1667">
                        {claim.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2" data-id="element-1668">
                    <Button size="sm" fullWidth onClick={() => handleAcceptClaim(claim)} className="bg-emerald-600 hover:bg-emerald-700 text-white border-none" data-id="element-1669">
                      Aceptar
                    </Button>
                    <Button size="sm" fullWidth variant="outline" onClick={() => handleRejectClaim(claim)} className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-900/20" data-id="element-1670">
                      Rechazar
                    </Button>
                  </div>
                </motion.div>)}
            </div>
          </div>}

        {/* Main Table Section */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-1671">
          {/* Filters Toolbar */}
          <div className="p-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-1672">
            <div className="flex flex-col lg:flex-row gap-4 justify-between items-center" data-id="element-1673">
              <div className="flex flex-wrap gap-3 w-full lg:w-auto" data-id="element-1674">
                <FilterDropdown value={statusFilter === 'all' ? 'Todos los estados' : statusFilter} options={['Todos los estados', 'Pendiente', 'Aceptada', 'Rechazada']} onChange={val => setStatusFilter(val === 'Todos los estados' ? 'all' : val)} className="w-full sm:w-48" data-id="element-1675" />
                <FilterDropdown value={priorityFilter === 'all' ? 'Todas las prioridades' : priorityFilter} options={['Todas las prioridades', 'Alta', 'Media', 'Baja']} onChange={val => setPriorityFilter(val === 'Todas las prioridades' ? 'all' : val)} className="w-full sm:w-48" data-id="element-1676" />
              </div>
              <div className="w-full lg:w-96 relative" data-id="element-1677">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1678" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar por orden, cliente, empresa..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-black dark:focus:ring-white outline-none transition-all" data-id="element-1679" />
              </div>
            </div>
          </div>

          {paginatedClaims.length === 0 ? <div className="p-20 text-center flex flex-col items-center justify-center" data-id="element-1680">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6" data-id="element-1681">
                <CheckCircle size={40} className="text-gray-300 dark:text-gray-600" data-id="element-1682" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1683">
                No hay reclamaciones
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto" data-id="element-1684">
                {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' ? 'No se encontraron resultados con los filtros actuales.' : '¡Todo en orden! No hay reclamaciones registradas por el momento.'}
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1685">
              <table className="w-full text-left border-collapse" data-id="element-1686">
                <thead className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-800" data-id="element-1687">
                  <tr data-id="element-1688">
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1689">
                      Orden / Fecha
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1690">
                      Cliente
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1691">
                      Problema
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1692">
                      Prioridad
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1693">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-1694">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1695">
                  {paginatedClaims.map(claim => <tr key={claim.id} className={`group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-l-4 ${getPriorityBorderColor(claim.priority)}`} data-id="element-1696">
                      <td className="px-6 py-4" data-id="element-1697">
                        <div className="flex flex-col" data-id="element-1698">
                          <span className="font-mono font-bold text-gray-900 dark:text-white text-base" data-id="element-1699">
                            #{claim.orderNo}
                          </span>
                          <span className="text-xs text-gray-500 mt-1 flex items-center gap-1" data-id="element-1700">
                            <Calendar size={10} data-id="element-1701" />
                            {new Date(claim.createdAt).toLocaleDateString('es-DO', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1702">
                        <div className="flex flex-col" data-id="element-1703">
                          <span className="font-bold text-gray-900 dark:text-white" data-id="element-1704">
                            {claim.customerName}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-id="element-1705">
                            <Building2 size={10} data-id="element-1706" /> {claim.company}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs" data-id="element-1707">
                        <div className="flex flex-col" data-id="element-1708">
                          <span className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-1709">
                            {claim.type}
                          </span>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1 group-hover:line-clamp-none transition-all" title={claim.description} data-id="element-1710">
                            {claim.description}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1711">
                        <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-bold border ${getPriorityBadge(claim.priority)}`} data-id="element-1712">
                          {claim.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-1713">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadge(claim.status)}`} data-id="element-1714">
                          {claim.status === 'Pendiente' && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-1715"></span>}
                          {claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-1716">
                        <div className="flex items-center justify-end gap-2" data-id="element-1717">
                          <button onClick={() => handleViewDetail(claim)} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Ver detalles" data-id="element-1718">
                            <Eye size={18} data-id="element-1719" />
                          </button>
                          {claim.status === 'Pendiente' && <>
                              <button onClick={() => handleAcceptClaim(claim)} className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Aceptar" data-id="element-1720">
                                <Check size={18} strokeWidth={2.5} data-id="element-1721" />
                              </button>
                              <button onClick={() => handleRejectClaim(claim)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Rechazar" data-id="element-1722">
                                <X size={18} strokeWidth={2.5} data-id="element-1723" />
                              </button>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}

          {totalPages > 1 && <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-1724">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredClaims.length} itemsPerPage={itemsPerPage} data-id="element-1725" />
            </div>}
        </div>
      </div>

      {/* MODALS */}
      <AnimatePresence data-id="element-1726">
        {/* Detail Modal */}
        {showDetailModal && selectedClaim && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-1727">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-1728">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50" data-id="element-1729">
                <div data-id="element-1730">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1731">
                    Detalle de Reclamación
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1732">
                    Orden #{selectedClaim.orderNo}
                  </p>
                </div>
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors" data-id="element-1733">
                  <X size={20} className="text-gray-500" data-id="element-1734" />
                </button>
              </div>

              <div className="p-6 space-y-6" data-id="element-1735">
                {/* Status Banner */}
                <div className={`p-4 rounded-xl border flex items-center gap-3 ${selectedClaim.status === 'Pendiente' ? 'bg-amber-50 border-amber-100 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200' : selectedClaim.status === 'Aceptada' ? 'bg-emerald-50 border-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-200' : 'bg-red-50 border-red-100 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'}`} data-id="element-1736">
                  {selectedClaim.status === 'Pendiente' ? <Clock size={20} data-id="element-1737" /> : selectedClaim.status === 'Aceptada' ? <CheckCircle size={20} data-id="element-1738" /> : <XCircle size={20} data-id="element-1739" />}
                  <div data-id="element-1740">
                    <p className="font-bold text-sm" data-id="element-1741">
                      Estado: {selectedClaim.status}
                    </p>
                    {selectedClaim.resolvedAt && <p className="text-xs opacity-80" data-id="element-1742">
                        Resuelto el{' '}
                        {new Date(selectedClaim.resolvedAt).toLocaleDateString()}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6" data-id="element-1743">
                  <div data-id="element-1744">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1745">
                      Cliente
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white" data-id="element-1746">
                      {selectedClaim.customerName}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1747">
                      {selectedClaim.company}
                    </p>
                  </div>
                  <div data-id="element-1748">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-1749">
                      Prioridad
                    </p>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${getPriorityBadge(selectedClaim.priority)}`} data-id="element-1750">
                      {selectedClaim.priority}
                    </span>
                  </div>
                </div>

                <div data-id="element-1751">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-1752">
                    Problema Reportado
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-1753">
                    <p className="font-bold text-sm text-gray-900 dark:text-white mb-1" data-id="element-1754">
                      {selectedClaim.type}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed" data-id="element-1755">
                      {selectedClaim.description}
                    </p>
                  </div>
                </div>

                {selectedClaim.resolution && <div data-id="element-1756">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-1757">
                      Resolución
                    </p>
                    <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-1758">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1759">
                        {selectedClaim.resolution}
                      </p>
                    </div>
                  </div>}
              </div>

              <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex justify-end gap-3" data-id="element-1760">
                {selectedClaim.status === 'Pendiente' ? <>
                    <Button variant="outline" onClick={() => {
                setShowDetailModal(false);
                handleRejectClaim(selectedClaim);
              }} data-id="element-1761">
                      Rechazar
                    </Button>
                    <Button onClick={() => {
                setShowDetailModal(false);
                handleAcceptClaim(selectedClaim);
              }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-1762">
                      Aceptar
                    </Button>
                  </> : <Button variant="outline" onClick={() => setShowDetailModal(false)} data-id="element-1763">
                    Cerrar
                  </Button>}
              </div>
            </motion.div>
          </div>}

        {/* Accept Modal */}
        {showAcceptModal && selectedClaim && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-1764">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-1765">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-emerald-50/50 dark:bg-emerald-900/10 flex items-center gap-4" data-id="element-1766">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-emerald-600 dark:text-emerald-400" data-id="element-1767">
                  <CheckCircle size={24} data-id="element-1768" />
                </div>
                <div data-id="element-1769">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1770">
                    Aceptar Reclamación
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1771">
                    Orden #{selectedClaim.orderNo}
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-5" data-id="element-1772">
                <div data-id="element-1773">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2" data-id="element-1774">
                    Resolución / Comentario
                  </label>
                  <textarea value={acceptResolution} onChange={e => setAcceptResolution(e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all" placeholder="Escribe la resolución para el cliente..." data-id="element-1775" />
                </div>

                <div className={`p-4 rounded-xl border transition-all ${applyRefund ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'}`} data-id="element-1776">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-1777">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${applyRefund ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-gray-300'}`} data-id="element-1778">
                      {applyRefund && <Check size={14} data-id="element-1779" />}
                    </div>
                    <input type="checkbox" checked={applyRefund} onChange={e => setApplyRefund(e.target.checked)} className="hidden" data-id="element-1780" />
                    <div className="flex-1" data-id="element-1781">
                      <span className="font-bold text-sm text-gray-900 dark:text-white block" data-id="element-1782">
                        Aplicar Reembolso
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-1783">
                        Devolver dinero al balance del empleado
                      </span>
                    </div>
                  </label>

                  {applyRefund && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800/50" data-id="element-1784">
                      <label className="block text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-1.5 uppercase" data-id="element-1785">
                        Monto a reembolsar
                      </label>
                      <div className="relative" data-id="element-1786">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-bold" data-id="element-1787">
                          RD$
                        </span>
                        <input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-gray-900 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="0.00" data-id="element-1788" />
                      </div>
                    </motion.div>}
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex gap-3" data-id="element-1789">
                <Button variant="outline" onClick={() => setShowAcceptModal(false)} fullWidth data-id="element-1790">
                  Cancelar
                </Button>
                <Button onClick={confirmAcceptClaim} fullWidth className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" data-id="element-1791">
                  Confirmar Aceptación
                </Button>
              </div>
            </motion.div>
          </div>}

        {/* Reject Modal */}
        {showRejectModal && selectedClaim && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-1792">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-1793">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-red-50/50 dark:bg-red-900/10 flex items-center gap-4" data-id="element-1794">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-400" data-id="element-1795">
                  <XCircle size={24} data-id="element-1796" />
                </div>
                <div data-id="element-1797">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1798">
                    Rechazar Reclamación
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1799">
                    Orden #{selectedClaim.orderNo}
                  </p>
                </div>
              </div>

              <div className="p-6 space-y-5" data-id="element-1800">
                <div data-id="element-1801">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2" data-id="element-1802">
                    Motivo del rechazo <span className="text-red-500" data-id="element-1803">*</span>
                  </label>
                  <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all" placeholder="Explica por qué se rechaza..." data-id="element-1804" />
                </div>

                <div className={`p-4 rounded-xl border transition-all ${applyRejectRefund ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'}`} data-id="element-1805">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-1806">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${applyRejectRefund ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300'}`} data-id="element-1807">
                      {applyRejectRefund && <Check size={14} data-id="element-1808" />}
                    </div>
                    <input type="checkbox" checked={applyRejectRefund} onChange={e => setApplyRejectRefund(e.target.checked)} className="hidden" data-id="element-1809" />
                    <div className="flex-1" data-id="element-1810">
                      <span className="font-bold text-sm text-gray-900 dark:text-white block" data-id="element-1811">
                        Reembolso Parcial (Opcional)
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-1812">
                        Reembolsar aunque se rechace la queja
                      </span>
                    </div>
                  </label>

                  {applyRejectRefund && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-800/50" data-id="element-1813">
                      <label className="block text-xs font-bold text-orange-700 dark:text-orange-400 mb-1.5 uppercase" data-id="element-1814">
                        Monto a reembolsar
                      </label>
                      <div className="relative" data-id="element-1815">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-600 font-bold" data-id="element-1816">
                          RD$
                        </span>
                        <input type="number" value={rejectRefundAmount} onChange={e => setRejectRefundAmount(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-orange-200 dark:border-orange-800 bg-white dark:bg-gray-900 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="0.00" data-id="element-1817" />
                      </div>
                    </motion.div>}
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex gap-3" data-id="element-1818">
                <Button variant="outline" onClick={() => setShowRejectModal(false)} fullWidth data-id="element-1819">
                  Cancelar
                </Button>
                <Button onClick={confirmRejectClaim} disabled={!rejectReason.trim()} fullWidth className="bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20" data-id="element-1820">
                  Confirmar Rechazo
                </Button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}